import React, { useState, useMemo } from 'react';
import { Sparkles, Check, X, Phone, Mail, Tag, Bell, Plus, Edit3, DollarSign, Shield, FileText } from 'lucide-react';
import api from '../lib/api';
import { todayISO } from '../lib/dateLocal';

const WE = {
  bgSurface: '#FFFFFF', border: '#E8E5E0', borderLight: '#F0EDE8',
  textPrimary: '#1C1917', textSecondary: '#57534E', textMuted: '#A8A29E',
  gold: '#C17F0A', accentBlue: '#1D4ED8', accentGreen: '#15803D', accentRed: '#DC2626', accentViolet: '#7C3AED',
};

// Ljudski prikaz akcije
function actionMeta(action) {
  switch (action) {
    case 'postavi_status': return { Icon: Tag, color: WE.gold, label: 'Status' };
    case 'zabiljezi_poziv': return { Icon: Phone, color: WE.accentGreen, label: 'Poziv' };
    case 'zabiljezi_email': return { Icon: Mail, color: WE.accentBlue, label: 'Email' };
    case 'podsjetnik': return { Icon: Bell, color: WE.accentBlue, label: 'Podsjetnik' };
    case 'predlozi_novi_status': return { Icon: Plus, color: WE.accentViolet, label: 'Novi status' };
    case 'kreiraj_ugovor': return { Icon: FileText, color: WE.accentBlue, label: 'Kreiraj ugovor' };
    case 'avans_placen': return { Icon: DollarSign, color: WE.accentGreen, label: 'Avans plaćen' };
    case 'puna_naplata': return { Icon: DollarSign, color: WE.accentGreen, label: 'Puna naplata' };
    case 'garancija': return { Icon: Shield, color: WE.gold, label: 'Garancija' };
    default: return { Icon: Sparkles, color: WE.textMuted, label: action };
  }
}

const COL_NAME = { status: 'Status kontakta', status_2: 'Interes' };
const OUTCOME = { answered: 'Javili se', no_answer: 'Nitko se nije javio' };

export default function NoteSuggestionsPopup({ data, statusConfig = [], onClose, onApply, onSnooze }) {
  // svaki prijedlog: { ...s, _on: true (odobren), params: editabilni }
  const [items, setItems] = useState(() => (data.suggestions || []).map(s => ({ ...s, _on: true, params: { ...s.params } })));
  const [saving, setSaving] = useState(false);
  const companyName = data.company_name || '';

  const statusVals = useMemo(() => {
    const m = {};
    statusConfig.forEach(c => { m[c.column_key] = (c.options || []).map(o => (typeof o === 'object' ? o.value : o)); });
    return m;
  }, [statusConfig]);

  const toggle = (i) => setItems(p => p.map((x, idx) => idx === i ? { ...x, _on: !x._on } : x));
  const setParam = (i, key, val) => setItems(p => p.map((x, idx) => idx === i ? { ...x, params: { ...x.params, [key]: val } } : x));

  const approve = () => {
    if (saving) return;
    setSaving(true);
    // OPTIMISTIČNO: popup se zatvara odmah, primjena ide u pozadini + toast potvrda.
    // (Parent uklanja iz reda i prikazuje "Primijenjeno za {firma}".)
    const approved = items.filter(x => x._on).map(x => ({ action: x.action, params: x.params }));
    onApply && onApply(approved);
  };

  const reject = async () => {
    try { await api.post('/leads/notes/reject', { suggestion_id: data.id }); } catch {}
    onClose();
  };

  const onCount = items.filter(x => x._on).length;

  return (
    // NAMJERNO bez zatvaranja na klik izvan / X — prijedlozi se ne smiju izgubiti bez odluke
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.45)' }}>
      <div style={{ background: WE.bgSurface, borderRadius: 14, width: '100%', maxWidth: 520, margin: '0 16px', maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${WE.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#C17F0A,#A86D00)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={16} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ fontSize: 15, fontWeight: 800, color: WE.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {companyName || 'AI prijedlozi iz bilješke'}
              </h3>
              <p style={{ fontSize: 11, color: WE.textMuted }}>
                {companyName ? 'AI prijedlozi iz bilješke — provjeri i odobri' : 'provjeri i odobri — ništa se ne primjenjuje bez tebe'}
              </p>
            </div>
          </div>
          <button onClick={onSnooze} title="Odgodi — spusti na dno stranice, prikazat će se ponovno (ništa se ne gubi)"
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, background: 'none', border: `1px solid ${WE.border}`, borderRadius: 8, cursor: 'pointer', color: WE.textSecondary, whiteSpace: 'nowrap' }}>
            ⏸ Kasnije
          </button>
        </div>

        {/* Lista prijedloga */}
        <div style={{ padding: '12px 20px', overflowY: 'auto', flex: 1 }}>
          {items.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: WE.textMuted, fontSize: 13 }}>AI nije našao akcije za ovu bilješku.</div>}
          {items.map((it, i) => {
            const m = actionMeta(it.action);
            const conf = it.params.confidence;
            return (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '11px 0', borderBottom: i < items.length - 1 ? `1px solid ${WE.borderLight}` : 'none', opacity: it._on ? 1 : 0.45 }}>
                <button onClick={() => toggle(i)} style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${it._on ? WE.accentGreen : WE.border}`, background: it._on ? WE.accentGreen : '#fff', cursor: 'pointer', flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {it._on && <Check size={13} color="#fff" />}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <m.Icon size={13} style={{ color: m.color }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: m.color }}>{m.label}</span>
                    {typeof conf === 'number' && <span style={{ fontSize: 10, color: WE.textMuted, marginLeft: 'auto' }}>{Math.round(conf * 100)}%</span>}
                  </div>

                  {/* Editabilni prikaz po tipu */}
                  {it.action === 'postavi_status' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <span style={{ color: WE.textSecondary }}>{COL_NAME[it.params.kolona] || it.params.kolona}:</span>
                      <select value={it.params.vrijednost} onChange={e => setParam(i, 'vrijednost', e.target.value)}
                        style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, color: WE.textPrimary, border: `1px solid ${WE.border}`, borderRadius: 6, fontFamily: 'inherit' }}>
                        {(statusVals[it.params.kolona] || [it.params.vrijednost]).map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  )}
                  {it.action === 'zabiljezi_poziv' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['answered', 'no_answer'].map(o => (
                        <button key={o} onClick={() => setParam(i, 'ishod', o)} style={{ padding: '3px 10px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', border: `1px solid ${it.params.ishod === o ? m.color : WE.border}`, background: it.params.ishod === o ? '#ECFDF5' : '#fff', color: it.params.ishod === o ? WE.accentGreen : WE.textMuted }}>{OUTCOME[o]}</button>
                      ))}
                    </div>
                  )}
                  {it.action === 'zabiljezi_email' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <input value={it.params.tekst || ''} onChange={e => setParam(i, 'tekst', e.target.value)}
                        placeholder="Opis emaila (npr. poslan inicijalni mail)"
                        style={{ padding: '4px 8px', fontSize: 12, border: `1px solid ${WE.border}`, borderRadius: 6, fontFamily: 'inherit', flex: 1, minWidth: 220 }} />
                    </div>
                  )}
                  {it.action === 'podsjetnik' && (() => {
                    // datum se čuva kao "YYYY-MM-DD" ili "YYYY-MM-DDTHH:MM" — razdvoji na datum + vrijeme
                    const raw = it.params.datum || '';
                    const dPart = raw.slice(0, 10);
                    const tMatch = raw.match(/[T ](\d{2}:\d{2})/);
                    const tPart = tMatch ? tMatch[1] : '';
                    const combine = (dp, tp) => setParam(i, 'datum', dp ? (tp ? `${dp}T${tp}` : dp) : '');
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <input value={it.params.tekst || ''} onChange={e => setParam(i, 'tekst', e.target.value)} style={{ padding: '4px 8px', fontSize: 12, border: `1px solid ${WE.border}`, borderRadius: 6, fontFamily: 'inherit' }} />
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <input type="date" value={dPart} onChange={e => combine(e.target.value, tPart)} style={{ padding: '4px 8px', fontSize: 12, border: `1px solid ${WE.border}`, borderRadius: 6, fontFamily: 'inherit', width: 150 }} />
                          <input type="time" value={tPart} onChange={e => combine(dPart || todayISO(), e.target.value)} title="Vrijeme podsjetnika" style={{ padding: '4px 8px', fontSize: 12, border: `1px solid ${WE.border}`, borderRadius: 6, fontFamily: 'inherit', width: 100 }} />
                          {!tPart && <span style={{ fontSize: 10, color: WE.textMuted }}>+ sat (opcionalno)</span>}
                        </div>
                      </div>
                    );
                  })()}
                  {it.action === 'predlozi_novi_status' && (
                    <div style={{ fontSize: 13 }}>
                      <span style={{ color: WE.textSecondary }}>{COL_NAME[it.params.kolona] || it.params.kolona}: </span>
                      <input value={it.params.predlozeni_naziv || ''} onChange={e => setParam(i, 'predlozeni_naziv', e.target.value)} style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, border: `1px solid ${WE.accentViolet}`, borderRadius: 6, fontFamily: 'inherit' }} />
                      <span style={{ fontSize: 10, color: WE.accentViolet, marginLeft: 6 }}>+ dodaje se kao nova opcija</span>
                    </div>
                  )}

                  {(it.action === 'avans_placen' || it.action === 'puna_naplata') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <span style={{ color: WE.textSecondary }}>Iznos (neto):</span>
                      <input type="number" value={it.params.iznos ?? ''} onChange={e => setParam(i, 'iznos', e.target.value === '' ? null : parseFloat(e.target.value))}
                        style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, color: WE.accentGreen, border: `1px solid ${WE.border}`, borderRadius: 6, width: 90, fontFamily: 'inherit' }} />
                      <span style={{ color: WE.textSecondary }}>€ neto</span>
                      <span style={{ fontSize: 10, color: WE.accentRed, marginLeft: 6 }}>⚠ neto iznos (PDV se dodaje sam) — provjeri</span>
                    </div>
                  )}
                  {it.action === 'garancija' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <span style={{ color: WE.textSecondary }}>Garancija:</span>
                      <input type="number" value={it.params.mjeseci ?? ''} onChange={e => setParam(i, 'mjeseci', e.target.value === '' ? null : parseInt(e.target.value))}
                        style={{ padding: '3px 8px', fontSize: 12, fontWeight: 700, border: `1px solid ${WE.border}`, borderRadius: 6, width: 60, fontFamily: 'inherit' }} />
                      <span style={{ color: WE.textSecondary }}>mjeseci{it.params.datum ? ` (ili ${it.params.datum})` : ''}</span>
                    </div>
                  )}
                  {it.action === 'kreiraj_ugovor' && (
                    <div style={{ fontSize: 12, color: WE.accentBlue }}>Firma nema ugovor — kreirat će se novi (status: aktivan)</div>
                  )}

                  {it.params.obrazlozenje && <div style={{ fontSize: 11, color: WE.textMuted, marginTop: 4, fontStyle: 'italic' }}>{it.params.obrazlozenje}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${WE.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <button onClick={() => { if (window.confirm('Sigurno ODBIJAŠ sve prijedloge? Statusi i upisi iz ove bilješke se NEĆE primijeniti.')) reject(); }}
            style={{ padding: '9px 14px', fontSize: 12, fontWeight: 600, color: WE.accentRed, background: 'none', border: '1px solid #FECACA', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>✕ Odbij sve</button>
          <button onClick={approve} disabled={saving || onCount === 0} style={{ padding: '9px 18px', fontSize: 13, fontWeight: 800, color: '#fff', background: onCount === 0 ? '#9CA3AF' : WE.accentGreen, border: 'none', borderRadius: 8, cursor: saving || onCount === 0 ? 'default' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={14} /> {saving ? 'Primjenjujem…' : `Odobri i primijeni (${onCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
